<template>
  <div class="filters-wrapper">
    <!-- Data Range com Datepicker -->
    <div class="filter">
      <DatePicker
        ref="datepicker"
        v-model="tempDateRange"
        range
        format="DD/MM/YYYY"
        :enable-time-picker="false"
        :partial-range="true"
        :auto-apply="false"
        :min-date="minSelectableDate"
        :max-date="maxSelectableDate"
        :disabled-date="isDateDisabled"
        :multi-calendars="false"
      >
        <template #input>
          <button class="filter-button" @click="openCalendar">
            {{ formattedDateRange }}
          </button>
        </template>
      </DatePicker>
    </div>

    <!-- Campanha -->
    <div class="filter">
      <div class="custom-dropdown">
        <button class="filter-button" @click="toggleDropdown('campaign')">
          {{
            selectedCampaigns.length
              ? `${selectedCampaigns.length} campanha(s)`
              : $t('CAMPAIGNS_REPORT.FILTERS.CAMPAIGN')
          }}
        </button>
        <div v-if="openDropdown === 'campaign'" class="dropdown-content">
          <input
            type="text"
            v-model="campaignSearch"
            class="search-input"
            :placeholder="$t('LABELS_REPORTS.FILTERS.TYPE_TO_SEARCH')"
          />
          <div v-for="c in filteredCampaigns" :key="c" class="checkbox-option">
            <label>
              <input
                type="checkbox"
                :value="c"
                v-model="selectedCampaigns"
                @change="emitFilters"
              />
              {{ c }}
            </label>
          </div>
        </div>
      </div>
    </div>

    <!-- Interação -->
    <div class="filter">
      <div class="custom-dropdown">
        <button class="filter-button" @click="toggleDropdown('interactions')">
          {{
            selectedInteractions.length
              ? `${selectedInteractions.length} interação(ões)`
              : $t('CAMPAIGNS_REPORT.FILTERS.INTERACTION')
          }}
        </button>
        <div v-if="openDropdown === 'interactions'" class="dropdown-content">
          <input
            type="text"
            v-model="interactionSearch"
            class="search-input"
            :placeholder="$t('LABELS_REPORTS.FILTERS.TYPE_TO_SEARCH')"
          />
          <div
            v-for="i in filteredInteractions"
            :key="i"
            class="checkbox-option"
          >
            <label>
              <input
                type="checkbox"
                :value="i"
                v-model="selectedInteractions"
                @change="emitFilters"
              />
              {{ i }}
            </label>
          </div>
        </div>
      </div>
    </div>

    <!-- Status da mensagem -->
    <div class="filter">
      <div class="custom-dropdown">
        <button class="filter-button" @click="toggleDropdown('status')">
          {{
            selectedStatuses.length
              ? `${selectedStatuses.length} status`
              : $t('CAMPAIGNS_REPORT.FILTERS.MESSAGE_STATUS')
          }}
        </button>
        <div v-if="openDropdown === 'status'" class="dropdown-content">
          <input
            type="text"
            v-model="statusSearch"
            class="search-input"
            :placeholder="$t('LABELS_REPORTS.FILTERS.TYPE_TO_SEARCH')"
          />
          <div v-for="s in filteredStatuses" :key="s" class="checkbox-option">
            <label>
              <input
                type="checkbox"
                :value="s"
                v-model="selectedStatuses"
                @change="emitFilters"
              />
              {{ s }}
            </label>
          </div>
        </div>
      </div>
    </div>

    <!-- Mensagem de erro -->
    <div class="filter">
      <div class="custom-dropdown">
        <button class="filter-button" @click="toggleDropdown('error')">
          {{
            selectedErrors.length
              ? `${selectedErrors.length} erro(s)`
              : $t('CAMPAIGNS_REPORT.FILTERS.MESSAGE_ERROR')
          }}
        </button>
        <div v-if="openDropdown === 'error'" class="dropdown-content">
          <input
            type="text"
            v-model="errorSearch"
            class="search-input"
            :placeholder="$t('LABELS_REPORTS.FILTERS.TYPE_TO_SEARCH')"
          />
          <div v-for="e in filteredErrors" :key="e" class="checkbox-option">
            <label>
              <input
                type="checkbox"
                :value="e"
                v-model="selectedErrors"
                @change="emitFilters"
              />
              {{ e }}
            </label>
          </div>
        </div>
      </div>
    </div>

    <!-- Inbox -->
    <div class="filter">
      <div class="custom-dropdown">
        <button class="filter-button" @click="toggleDropdown('inbox')">
          {{
            selectedInboxes.length
              ? $t('LABELS_REPORTS.FILTERS.SELECTED_INBOXES', {
                  count: selectedInboxes.length,
                })
              : $t('LABELS_REPORTS.FILTERS.INBOX')
          }}
        </button>
        <div v-if="openDropdown === 'inbox'" class="dropdown-content">
          <input
            type="text"
            v-model="inboxSearch"
            class="search-input"
            :placeholder="$t('LABELS_REPORTS.FILTERS.INBOX_SEARCH_PLACEHOLDER')"
          />
          <div
            v-for="inbox in filteredInboxes"
            :key="inbox.name"
            class="checkbox-option"
          >
            <label>
              <input
                type="checkbox"
                :value="inbox.name"
                v-model="selectedInboxes"
                @change="emitFilters"
              />
              {{ inbox.name }}
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';

export default {
  name: 'CampaignsFilters',
  components: { DatePicker },
  props: {
    campaignsData: { type: Array, required: true },
  },
  data() {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);

    return {
      tempDateRange: [sevenDaysAgo, today],
      customDateRange: [sevenDaysAgo, today],
      selectedInboxes: [],
      selectedCampaigns: [],
      selectedStatuses: [],
      selectedErrors: [],
      selectedInteractions: [],
      inboxSearch: '',
      campaignSearch: '',
      statusSearch: '',
      errorSearch: '',
      interactionSearch: '',
      openDropdown: null,
    };
  },
  computed: {
    inboxes() {
      const unique = {};
      return this.campaignsData
        .map(item => ({ name: item.inbox_name }))
        .filter(inbox => {
          if (!inbox.name || unique[inbox.name]) return false;
          unique[inbox.name] = true;
          return true;
        });
    },
    filteredInboxes() {
      return this.inboxes.filter(i =>
        i.name.toLowerCase().includes(this.inboxSearch.toLowerCase())
      );
    },
    filteredCampaigns() {
      const campaigns = this.campaignsData
        .map(item => item.campaign_title || '')
        .filter(Boolean);
      const unique = [...new Set(campaigns)];
      return unique.filter(c =>
        c.toLowerCase().includes(this.campaignSearch.toLowerCase())
      );
    },
    filteredStatuses() {
      const statuses = this.campaignsData
        .map(item => item.status_message || '')
        .filter(Boolean);
      const unique = [...new Set(statuses)];
      return unique.filter(s =>
        s.toLowerCase().includes(this.statusSearch.toLowerCase())
      );
    },
    filteredErrors() {
      const errors = this.campaignsData
        .map(item => item.message_error || '')
        .filter(Boolean);
      const unique = [...new Set(errors)];
      return unique.filter(e =>
        e.toLowerCase().includes(this.errorSearch.toLowerCase())
      );
    },
    filteredInteractions() {
      const interactions = this.campaignsData
        .map(item => item.interacted || '')
        .filter(Boolean);
      const unique = [...new Set(interactions)];
      return unique.filter(i =>
        i.toLowerCase().includes(this.interactionSearch.toLowerCase())
      );
    },
    formattedDateRange() {
      const [start, end] = this.tempDateRange;
      if (!start || !end)
        return this.$t('LABELS_REPORTS.FILTERS.SELECT_DATE_RANGE');
      const format = d =>
        new Date(d).toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
      return `${format(start)} ~ ${format(end)}`;
    },
    today() {
      return new Date();
    },
    minSelectableDate() {
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(this.today.getFullYear() - 1);
      return oneYearAgo;
    },
    maxSelectableDate() {
      return this.today;
    },
  },
  watch: {
    tempDateRange(newRange) {
      if (newRange[0] && newRange[1]) {
        this.customDateRange = newRange;
        this.resetFiltersExceptDate();
        this.emitFilters();
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    emitFilters() {
      this.$emit('filter-change', {
        customDateRange: this.customDateRange,
        inboxNames: this.selectedInboxes,
        campaignTitles: this.selectedCampaigns,
        statusMessages: this.selectedStatuses,
        errorMessages: this.selectedErrors,
        interactions: this.selectedInteractions,
      });
    },
    resetFiltersExceptDate() {
      this.selectedInboxes = [];
      this.selectedCampaigns = [];
      this.selectedStatuses = [];
      this.selectedErrors = [];
      this.selectedInteractions = [];
      this.inboxSearch = '';
      this.campaignSearch = '';
      this.statusSearch = '';
      this.errorSearch = '';
      this.interactionSearch = '';
    },
    toggleDropdown(dropdownName) {
      this.openDropdown =
        this.openDropdown === dropdownName ? null : dropdownName;
    },
    openCalendar() {
      this.$refs.datepicker.openMenu?.();
    },
    handleClickOutside(event) {
      const wrapper = this.$el;
      if (!wrapper || !wrapper.contains(event.target)) {
        this.openDropdown = null;
      }
    },
    isDateDisabled(date) {
      const min = this.minSelectableDate;
      const max = this.maxSelectableDate;
      return date < min || date > max;
    },
  },
};
</script>

<style>
.filters-wrapper {
  width: 280px;
  min-height: 150vh;
  background-color: #000;
  padding: 20px;
  box-sizing: border-box;
  color: white;
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex-shrink: 0;
}

.filter {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.filter-button {
  width: 240px;
  height: 50px;
  background: black !important;
  color: white !important;
  border: 1px solid white !important;
  padding: 10px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 4px;
  cursor: pointer;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-sizing: border-box;
  appearance: none;
}

.filter-button:hover {
  background: #1a1a1a;
}

.hidden-datepicker-input {
  display: none !important;
}

::v-deep(.dp__input_icon) {
  display: none !important;
}

.custom-dropdown {
  position: relative;
  width: 240px;
}

.dropdown-content {
  position: absolute;
  top: 56px;
  background: #000;
  border: 1px solid white;
  width: 100%;
  max-height: 300px;
  overflow-y: auto;
  z-index: 999;
  padding: 10px;
  border-radius: 4px;
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.1);
}

.search-input {
  width: 100%;
  padding: 6px;
  margin-bottom: 10px;
  border-radius: 4px;
  border: 1px solid #ccc;
  box-sizing: border-box;
}

.checkbox-option {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  gap: 8px;
}

.checkbox-option label {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  color: white;
}

.checkbox-option input[type='checkbox'] {
  margin: 0;
}

.count {
  color: #bbb;
  font-size: 12px;
  white-space: nowrap;
}

.mx-icon-calendar {
  display: none !important;
}

.mx-icon-clear {
  display: none !important;
}
</style>
